import { StandardEvents } from '@shopify/events';

/**
 * SND-19800 — product code under the PDP title.
 * Copies the SKU on click (button or the code itself) and follows the selected
 * variant: the variant picker's ProductSelectEvent resolves with the refetched
 * section HTML, where our counterpart already carries the new variant's SKU.
 */
class SoundiumSku extends HTMLElement {
  /** @type {number | undefined} */
  #resetTimer;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.closest('.shopify-section, dialog')?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    clearTimeout(this.#resetTimer);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-sku-copy], [data-sku-code]')) return;
    const sku = this.dataset.sku;
    if (sku) this.#copy(sku);
  };

  /** @param {string} text */
  async #copy(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.append(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }

    const status = this.querySelector('[data-sku-status]');
    const button = this.querySelector('[data-sku-copy]');
    if (status instanceof HTMLElement && button instanceof HTMLElement) {
      status.textContent = button.dataset.copiedLabel || '';
    }
    this.setAttribute('data-copied', '');
    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => {
      this.removeAttribute('data-copied');
      if (status) status.textContent = '';
    }, 1600);
  }

  /** @param {Event & {promise?: Promise<any>}} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const updated = html.querySelector('soundium-sku');
        if (!(updated instanceof HTMLElement)) return;

        const sku = updated.dataset.sku || '';
        this.dataset.sku = sku;
        this.hidden = !sku;
        const code = this.querySelector('[data-sku-code]');
        if (code) code.textContent = sku;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[soundium-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('soundium-sku')) {
  customElements.define('soundium-sku', SoundiumSku);
}
